import { firstOfMonth, lastOfMonth } from '@/lib/dates'
import { useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import {
  EmptyState, ErrorState, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useMyRegularizations, useOpenRegularizations, useRegularizationMutations } from '../api'
import { RegularisationForm } from '../components/RegularisationForm'
import { RegularisationApproval } from '../components/RegularisationApproval'
import { REGULARIZATION_TYPE_LABELS } from '../types'
import type { RegularizationRequestView } from '../types'
import { cn } from '@/lib/utils'
import { DatePicker } from '@/components/ui/date-picker'

type Tab = 'mine' | 'open'

/**
 * Corrections to attendance that the terminals got wrong.
 *
 * <p>Two audiences on one screen. An employee sees their own corrections and raises new
 * ones; somebody with the approver permission sees everything still open in the period —
 * which is the same list that holds up the payroll close, so it is worth looking at before
 * month end rather than being surprised by it.
 */
export function RegularisationPage() {
  useBreadcrumb()
  const [tab, setTab] = useState<Tab>('mine')
  const [from, setFrom] = useState(firstOfMonth())
  const [to, setTo] = useState(lastOfMonth())
  const [raising, setRaising] = useState(false)
  const [newDate, setNewDate] = useState('')
  const [deciding, setDeciding] = useState<string | null>(null)

  const mine = useMyRegularizations(from, to)
  const open = useOpenRegularizations(from, to)
  const { cancel } = useRegularizationMutations()

  const active = tab === 'mine' ? mine : open

  return (
    <div className="space-y-6">
      <PageHeader
        title="Regularisation"
        subtitle="Ask for a day's attendance to be corrected when a punch is missing or wrong."
        actions={
          <Button type="button" onClick={() => setRaising((open) => !open)}>
            <Plus className="size-4" aria-hidden="true" />
            Correct a day
          </Button>
        }
      />

      {raising && (
        <Card className="space-y-4 p-5">
          <div className="space-y-1.5">
            <Label htmlFor="reg-date" required>
              Which day
            </Label>
            <DatePicker
              id="reg-date"
              value={newDate}
              onChange={(event) => setNewDate(event.target.value)}
              className="w-52"
            />
          </div>
          {newDate ? (
            <RegularisationForm
              workDate={newDate}
              onDone={() => {
                setRaising(false)
                setNewDate('')
              }}
            />
          ) : (
            <p className="text-sm text-[var(--color-muted)]">
              Pick the day first. The day has to have been processed before it can be corrected.
            </p>
          )}
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <nav className="flex gap-1" aria-label="Regularisation views">
          <TabButton active={tab === 'mine'} onClick={() => setTab('mine')}>
            My corrections
          </TabButton>
          <PermissionGate permission="attendance:approve">
            <TabButton active={tab === 'open'} onClick={() => setTab('open')}>
              Still open
            </TabButton>
          </PermissionGate>
        </nav>
        <div className="ml-auto flex gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="range-from">From</Label>
            <DatePicker id="range-from" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="range-to">To</Label>
            <DatePicker id="range-to" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </div>

      {tab === 'open' && (open.data?.length ?? 0) > 0 && (
        <p className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--color-tint-orange)] px-3 py-2.5 text-sm text-[var(--color-body)]">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" aria-hidden="true" />
          <span>
            {open.data?.length} correction{open.data?.length === 1 ? '' : 's'} still open in this
            range. The payroll period cannot be closed until each one is decided.
          </span>
        </p>
      )}

      <Card>
        {active.isPending && <Skeleton className="m-4 h-32" />}
        {active.isError && <ErrorState error={active.error} onRetry={() => void active.refetch()} />}
        {active.data?.length === 0 && (
          <EmptyState
            title="Nothing to correct"
            description={
              tab === 'mine'
                ? 'Your attendance for this range has no outstanding corrections.'
                : 'Every correction in this range has been decided.'
            }
          />
        )}
        {(active.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[48rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Day</th>
                  {tab === 'open' && <th scope="col" className="p-3 font-medium">Employee</th>}
                  <th scope="col" className="p-3 font-medium">Problem</th>
                  <th scope="col" className="p-3 font-medium">Raised by</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {active.data?.map((request) => (
                  <RequestRow
                    key={request.id}
                    request={request}
                    showEmployee={tab === 'open'}
                    onDecide={() => setDeciding(request.id)}
                    onCancel={() => void cancel.mutateAsync({ id: request.id })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {deciding && (
        <Card className="space-y-4 p-5">
          <RegularisationApproval requestId={deciding} onDecided={() => setDeciding(null)} />
          <Button type="button" variant="ghost" onClick={() => setDeciding(null)}>
            Close
          </Button>
        </Card>
      )}
    </div>
  )
}

function RequestRow({
  request,
  showEmployee,
  onDecide,
  onCancel,
}: {
  request: RegularizationRequestView
  showEmployee: boolean
  onDecide: () => void
  onCancel: () => void
}) {
  const live = request.status === 'PENDING' || request.status === 'SUBMITTED' || request.status === 'DRAFT'

  return (
    <tr className="border-b border-[var(--color-border)] last:border-0">
      <td className="p-3 whitespace-nowrap">{new Date(request.workDate).toLocaleDateString()}</td>
      {showEmployee && <td className="p-3">{request.employeeName}</td>}
      <td className="p-3">{REGULARIZATION_TYPE_LABELS[request.requestType]}</td>
      <td className="p-3 text-[var(--color-muted)]">
        {request.origin === 'SYSTEM' ? 'Found automatically' : request.origin === 'MANAGER' ? 'Manager' : 'Employee'}
      </td>
      <td className="p-3">
        <StatusBadge status={request.status} />
      </td>
      <td className="p-3 text-right">
        <div className="flex justify-end gap-2">
          <PermissionGate permission="attendance:approve">
            <Button type="button" size="sm" variant="secondary" onClick={onDecide}>
              Review
            </Button>
          </PermissionGate>
          {live && (
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              Withdraw
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'rounded-[var(--radius-control)] px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
          : 'text-[var(--color-muted)] hover:bg-[var(--color-page)]',
      )}
    >
      {children}
    </button>
  )
}
